import {
  Autocomplete,
  FormHelperText,
  TextField,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { selectField } from '../../selectors'
import { setValue } from '../../slice'
import type { FieldName, MultiTextValue } from '../../types'
import validationChecks from '../../validation'

type MultiTextProps = {
  fieldName: FieldName
}

export default function MultiText(props: MultiTextProps) {
  const { fieldName } = props
  const dispatch = useAppDispatch()
  const fieldData = useAppSelector(selectField(fieldName))
  const [inputValue, setInputValue] = useState('')
  const [errorMessages, setErrorMessages] = useState<string[]>([])

  const { label } = fieldData.field

  const validateFunctions = validationChecks[fieldName] || []

  const handleChange = (
    _event: React.SyntheticEvent,
    values: MultiTextValue,
    reason: string,
  ) => {
    const reasonsToValidate = ['createOption', 'blur']

    function modifyBeforeAdd() {
      const disallowedSymbols = /[;,]/g
      if (!reasonsToValidate.includes(reason)) return values

      let newValue = values.pop()
      if (newValue === undefined) return values

      newValue = newValue.replace(disallowedSymbols, '')
      newValue = newValue?.trim()
      if (!values.includes(newValue)) values.push(newValue)
      values = values.filter((v) => v !== '')
    }

    function handleValidation() {
      const errorMessages: string[] = []
      const newestValue = values.slice(-1)[0]
      const v = validateFunctions.map((func) => func.validate(values))

      Promise.all(v)
        .then(() => {
          setErrorMessages([])
          dispatch(setValue({ fieldName: fieldName, value: values }))
        })
        .catch((error) => {
          errorMessages.push(error.message)
          setErrorMessages(errorMessages)
          setInputValue(newestValue)
        })
    }

    if (reasonsToValidate.includes(reason)) {
      modifyBeforeAdd()
      handleValidation()
    } else {
      dispatch(setValue({ fieldName: fieldName, value: values }))
    }
  }

  const handleInputChange = (_event: React.SyntheticEvent, value: string) => {
    setInputValue(value)
  }

  const value = fieldData.value as MultiTextValue

  return (
    <>
      <Autocomplete
        key={fieldName}
        {...{ name: fieldName, label, value }}
        multiple={true}
        freeSolo={true}
        options={value}
        open={false}
        autoSelect={true}
        onChange={handleChange}
        onInputChange={handleInputChange}
        inputValue={inputValue}
        renderInput={(params) => <TextField label={label} {...params} />}
      />
      <Typography variant="caption">Press enter to add tag.</Typography>
      <FormHelperText>{errorMessages}</FormHelperText>
    </>
  )
}
